import type { RateSlabRow } from '@/api/masters';
import { orderBands } from '@core/bookings/pricing';

/**
 * Bands, grouped back into the cards a shop actually thinks in.
 *
 * <p>
 * The API is band-level — one row per "up to 3 days, ₹6,500" — because that is the grain a rate is
 * published and withdrawn at. But nobody sets *a band*: a shop writes a card, "Ethnic Wear costs
 * this much for this long", and the ladder only means anything read whole. A list of loose bands
 * would show a screen full of rows whose relationship to each other is the entire point and is
 * nowhere on it.
 * </p>
 *
 * <p>
 * <strong>The scope is part of a card's identity, not a column on it.</strong> Resolution takes the
 * branch-local rows at a level if there are any and the shared ones otherwise —
 * `RateSlabService.Best`, all-or-nothing — so a shared card and a branch-local card for the
 * same category are two different cards, and at this branch only one of them is in force. Grouping
 * them together would draw a six-band ladder that no booking is ever priced from.
 * </p>
 *
 * <p>
 * Pure and tested, in the shape `bookings/draft.ts` and `units/intake.ts` established: the screen
 * renders what this decides and holds no rules of its own.
 * </p>
 */

/**
 * The four levels a card can be written at, narrowest last.
 *
 * Resolution runs unit → variant → item → category → ancestors, and the nearest level with any
 * active band wins the whole card. Which modifiers still apply depends on where it stopped: a card
 * at the configuration already knows the size, one at the garment knows everything.
 */
export type OwnerKind = 'Category' | 'Item' | 'Variant' | 'Unit';

export interface RateCard {
  /** Stable across refetches — the owner and the scope, which are exactly what identifies a card. */
  key: string;
  ownerKind: OwnerKind;
  ownerId: number;
  ownerName: string;
  /** Company-wide, or local to the branch being acted at. */
  shared: boolean;
  /** Every band, retired ones included, in the order the pricer reads them. */
  bands: RateSlabRow[];
  /** The bands that actually price anything. A retired band is skipped by resolution. */
  activeBands: RateSlabRow[];
  /**
   * True when this branch has published its own card for the same owner, so this shared one prices
   * nothing here. Invisible in the data — the rows look perfectly healthy — and the reason a
   * company rate somebody has just edited can appear to have no effect at all.
   */
  shadowed: boolean;
  /** The other side of the same fact: this branch-local card is displacing the company's. */
  overrides: boolean;
}

/** The owner and the scope, which together are what makes two bands part of one card. */
export function cardKeyFor(ownerKind: OwnerKind, ownerId: number, shared: boolean): string {
  return `${ownerKind}:${ownerId}:${shared ? 'shared' : 'branch'}`;
}

/**
 * Which of the four owners a band names, and what that owner is called.
 *
 * Read narrowest first, matching the resolution order — the server guarantees exactly one is set, so
 * the order only decides which name is read, not which card the band lands in.
 */
function ownerOf(
  row: RateSlabRow,
): { ownerKind: OwnerKind; ownerId: number; ownerName: string } | null {
  if (row.itemUnitId !== null) {
    return {
      ownerKind: 'Unit',
      ownerId: row.itemUnitId,
      ownerName: row.itemUnitSerialCode ?? `#${String(row.itemUnitId)}`,
    };
  }

  if (row.variantId !== null) {
    return {
      ownerKind: 'Variant',
      ownerId: row.variantId,
      ownerName: row.variantName ?? `#${String(row.variantId)}`,
    };
  }

  if (row.itemId !== null) {
    return {
      ownerKind: 'Item',
      ownerId: row.itemId,
      ownerName: row.itemName ?? `#${String(row.itemId)}`,
    };
  }

  if (row.categoryId !== null) {
    return {
      ownerKind: 'Category',
      ownerId: row.categoryId,
      ownerName: row.categoryName ?? `#${String(row.categoryId)}`,
    };
  }

  return null;
}

/** Broadest first, which is the order a shop reads its own pricing in. */
const ownerOrder: Record<OwnerKind, number> = { Category: 0, Item: 1, Variant: 2, Unit: 3 };

/** How each level reads on the screen — the shop's words, not the schema's. */
export const ownerLabels: Record<OwnerKind, string> = {
  Category: 'Category',
  Item: 'One design',
  Variant: 'One configuration',
  Unit: 'One garment',
};

/**
 * Every visible band, as cards.
 *
 * Categories before designs, because that is the order a shop reads its own pricing in: the general
 * rule, then the exceptions to it. Within each, by name.
 *
 * A row that names neither an item nor a category cannot belong to a card and is dropped rather
 * than filed under a made-up owner — the server refuses to create one, so this is defence against a
 * shape that should not exist rather than a case with a right answer.
 */
export function groupIntoCards(rows: readonly RateSlabRow[]): RateCard[] {
  const cards = new Map<string, RateCard>();

  for (const row of rows) {
    const owner = ownerOf(row);

    if (owner === null) {
      continue;
    }

    const { ownerKind, ownerId, ownerName } = owner;
    const key = cardKeyFor(ownerKind, ownerId, row.shared);

    const card = cards.get(key) ?? {
      key,
      ownerKind,
      ownerId,
      ownerName,
      shared: row.shared,
      bands: [],
      activeBands: [],
      shadowed: false,
      overrides: false,
    };

    card.bands.push(row);
    cards.set(key, card);
  }

  const all = [...cards.values()];

  for (const card of all) {
    card.bands = orderBands(card.bands);
    card.activeBands = card.bands.filter((band) => band.isActive);

    // Only an *active* local card displaces the company's. One whose bands have all been retired
    // prices nothing, and resolution falls through to the shared card rather than to nothing —
    // marking it shadowed would send a shop looking for an override that no longer applies.
    const counterpart = all.find(
      (other) =>
        other.ownerKind === card.ownerKind &&
        other.ownerId === card.ownerId &&
        other.shared !== card.shared &&
        other.bands.some((band) => band.isActive),
    );

    card.shadowed = card.shared && counterpart !== undefined;
    card.overrides = !card.shared && counterpart !== undefined;
  }

  return all.sort((a, b) => {
    if (a.ownerKind !== b.ownerKind) {
      return ownerOrder[a.ownerKind] - ownerOrder[b.ownerKind];
    }

    return a.ownerName.localeCompare(b.ownerName);
  });
}

/**
 * What is wrong with a card, in the words a shop would use.
 *
 * <p>
 * Both of these are refusals waiting to happen at a counter rather than cosmetic gaps. A booking
 * whose duration no band covers is rejected outright — `BookingService` refuses an unpriced garment
 * instead of summing it as zero — so the customer is standing there while a clerk works out why the
 * screen will not take their wedding. Saying it here, on the screen that can fix it, is the only
 * place it is cheap.
 * </p>
 */
export function cardProblems(card: RateCard): string[] {
  if (card.activeBands.length === 0) {
    return ['Every band is retired — this card prices nothing.'];
  }

  const open = card.activeBands.find((band) => band.upToDays === null);

  if (open) {
    return [];
  }

  const longest = card.activeBands.reduce((days, band) => Math.max(days, band.upToDays ?? 0), 0);

  return [
    `Nothing priced past ${String(longest)} days — a longer booking is refused rather than charged.`,
  ];
}

/** One rung of the ladder: the band, and the span of days it answers for. */
export interface LadderStep {
  band: RateSlabRow;
  /** First day this band covers. */
  from: number;
  /** Last day, or null on the open band. */
  to: number | null;
}

/**
 * The card as a ladder of day ranges.
 *
 * A band stores only its upper bound, so "4–7 days" exists nowhere in the data — it is the previous
 * band's bound plus one. Deriving it is what turns three prices into something a shop can check
 * against what it meant to charge.
 */
export function ladder(bands: readonly RateSlabRow[]): LadderStep[] {
  let from = 1;

  return orderBands(bands).map((band) => {
    const step: LadderStep = { band, from, to: band.upToDays };

    if (band.upToDays !== null) {
      from = band.upToDays + 1;
    }

    return step;
  });
}

/** "1–3 days", "8 days and beyond" — how the rung reads on the card. */
export function stepLabel(step: LadderStep): string {
  if (step.to === null) {
    return step.from === 1 ? 'Any duration' : `${String(step.from)} days and beyond`;
  }

  return step.from === step.to
    ? `${String(step.to)} day${step.to === 1 ? '' : 's'}`
    : `${String(step.from)}–${String(step.to)} days`;
}

/**
 * Whether a bound is already taken on a card.
 *
 * The server answers this with a 409, and it has to — two clients can publish at once. Asking here
 * as well is what turns "Conflict" after a save into a disabled button and a sentence before one,
 * on the field that caused it. Retired bands count: they still occupy the bound, and the fix is to
 * reinstate the band rather than to add a second one beside it.
 */
export function boundIsTaken(
  bands: readonly RateSlabRow[],
  upToDays: number | null,
  exceptId?: number,
): boolean {
  return bands.some((band) => band.upToDays === upToDays && band.id !== exceptId);
}
